import {
  ChargeCardPaymentFrequency,
  ChargeCardPaymentSourceOption,
  InlineAlertType,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useMakePayment } from 'features/believe/makePayment/hooks/useMakePayment';
import { useSubmitPayCard } from 'features/believe/makePayment/hooks/useSubmitPayCard';
import PaymentError from 'features/believe/makePayment/PaymentError/PaymentError';
import PaymentSuccess from 'features/believe/makePayment/PaymentSuccess/PaymentSuccess';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import CalendarModalPicker from 'shared/components/vxl/CalendarPicker/CalendarModalPicker';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import RadioButton from 'shared/components/vxl/selection/RadioButton/RadioButton';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isAndroid, isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { useIdempotencyToken } from 'shared/utils/idempotencyTokens/idempotencyTokenUtils';
import {
  changeDateFormat,
  DATE_INPUT_MAXLENGTH,
  formatDateWithSlash,
} from 'shared/utils/string/date';

enum MakePaymentFlowEnum {
  Payment = 'PAYMENT',
  Success = 'SUCCESS',
  Error = 'ERROR',
}

const SchedulePaymentScreen = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();
  const { accountId, sessionId } = useLocalSearchParams<{
    accountId: string;
    sessionId: string;
  }>();
  const [primaryButtonDisable, setPrimaryButtonDisable] = useState(true);
  const [radioButtonSelected, isRadioButtonSelected] = useState(false);
  const [selectedValue, setSelectedValue] =
    useState<ChargeCardPaymentSourceOption>();
  const [calendarModalVisible, isCalendarModalVisible] = useState(false);
  const { idempotencyToken, rotateToken } = useIdempotencyToken(sessionId);

  const [bannerText, setBannerText] = useState<string>('');

  const [paymentFlow, setPaymentFlow] = useState<MakePaymentFlowEnum>(
    MakePaymentFlowEnum.Payment,
  );

  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'makePayment',
  });

  const makePaymentContext = useMakePayment();
  const { fetching, submitPayCard } = useSubmitPayCard();
  const options = makePaymentContext?.options || [];
  const isDue = makePaymentContext?.isPaymentDue;
  const fullName = makePaymentContext?.fullName || '';
  const [paymentDate, setPaymentDate] = useState('');
  const [id, setId] = useState('');

  const handleCalenderAnalytics = usePressableAnalytics({
    analyticsId: 'selectDate',
  });
  const [selectedAmount, setSelectedAmount] = useState('');

  const handleRadioPress = (value: ChargeCardPaymentSourceOption) => {
    setSelectedAmount(value?.amount?.amount as string);
    isRadioButtonSelected(true);
    setSelectedValue(value);
    setId(value.id);
    let bannerMessage = 'alertMessage_default';

    if (makePaymentContext?.isSecuredAccount) {
      bannerMessage = 'alertMessage_secured';
    } else if (
      !isDue &&
      value.frequency === ChargeCardPaymentFrequency.Scheduled
    ) {
      bannerMessage = 'alertMessage_scheduled';
    }

    const amount = value?.amount?.formatted ?? '$0.00';
    const message = translatedText(bannerMessage, {
      PAYMENT_AMOUNT: amount,
      FULL_NAME: fullName,
    });
    setBannerText(message);
  };

  const handlePayCardButtonClick = async () => {
    const typeName = await submitPayCard(idempotencyToken, id, paymentDate);

    if (typeName === Typenames.SubmitChargeCardPaymentOptionSuccess) {
      setPaymentFlow(MakePaymentFlowEnum.Success);
      rotateToken();
    } else {
      setPaymentFlow(MakePaymentFlowEnum.Error);
    }
  };

  const handleBackAction = () => {
    setPaymentFlow(MakePaymentFlowEnum.Payment);
  };

  const isOneTimeFrequency =
    selectedValue?.frequency === ChargeCardPaymentFrequency.OneTime;

  useEffect(() => {
    if (isOneTimeFrequency) {
      setPrimaryButtonDisable(!(paymentDate && radioButtonSelected));
    } else {
      setPrimaryButtonDisable(!radioButtonSelected);
    }
  }, [radioButtonSelected, selectedValue, paymentDate, isOneTimeFrequency]);

  if (paymentFlow === MakePaymentFlowEnum.Success) {
    return <PaymentSuccess />;
  } else if (paymentFlow === MakePaymentFlowEnum.Error) {
    return (
      <PaymentError accountId={accountId} handleBackAction={handleBackAction} />
    );
  }

  return (
    <View
      style={[
        styles.screen,
        { paddingBottom: bottom + (isWeb || isAndroid ? theme.spacing.m : 0) },
      ]}
    >
      <ScrollView contentContainerStyle={styles.containerStyle}>
        <Stack.Screen
          options={{
            title: translatedText('makePayment'),
            headerBackVisible: true,
            headerShown: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} />
            ),
          }}
        />
        <TitleLgText style={styles.titleStyle} numberOfLines={0}>
          {translatedText('payFromYourBelieveAccount')}
        </TitleLgText>
        {options.map((item) => (
          <View key={item.id}>
            <RadioButton
              key={item.id}
              labelText={item.description}
              checked={selectedValue?.id === item.id}
              onPress={() => handleRadioPress(item)}
              id={item.id}
            />
          </View>
        ))}
        {radioButtonSelected ? (
          <View style={styles.buttonStyle}>
            {isOneTimeFrequency ? (
              <View style={styles.input}>
                <VXLTextInput
                  testID="datePicker"
                  labelText=""
                  placeholder={translatedText('paymentDate')}
                  value={formatDateWithSlash(changeDateFormat(paymentDate))}
                  maxLength={DATE_INPUT_MAXLENGTH}
                  onFocus={handleCalenderAnalytics}
                  readOnly
                  showSoftInputOnFocus={false}
                  caretHidden
                  rightIcon={{
                    iconKey: 'CALENDAR',
                    iconAccessibilityLabel: 'date-icon',
                    onPress: () => {
                      handleCalenderAnalytics();
                      isCalendarModalVisible(true);
                    },
                  }}
                />
              </View>
            ) : null}

            <InlineAlert
              alertType={InlineAlertType.Info}
              isImageVisible={false}
              bodyText={bannerText}
            />
          </View>
        ) : null}
        <View style={styles.bottomContainer}>
          <View style={styles.buttonStyle}>
            <Button
              label={translatedText('buttons.payCard')}
              type="PRIMARY"
              isDisabled={primaryButtonDisable}
              isLoading={fetching}
              onPress={handlePayCardButtonClick}
              analyticsId="payCardClick"
              customAttributes={{ amount: selectedAmount }}
            />
          </View>
          <View style={styles.buttonStyle}>
            <Button
              label={translatedText('buttons.cancel')}
              type="SECONDARY"
              onPress={() => {
                router.navigate({
                  pathname: '/accounts/[id]',
                  params: { id: accountId },
                });
              }}
              analyticsId="cancelClick"
            />
          </View>
        </View>
      </ScrollView>
      {calendarModalVisible ? (
        <CalendarModalPicker
          calendarModalVisible={calendarModalVisible}
          userSelectedDate={paymentDate}
          getSelectedDate={(date) => {
            isCalendarModalVisible(false);
            setPaymentDate(date);
          }}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  bottomContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  buttonStyle: {
    marginTop: theme.spacing.m,
  },
  containerStyle: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  input: {
    marginVertical: theme.spacing.m,
    width: '100%',
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  titleStyle: {
    paddingBottom: theme.spacing.m,
  },
});

export default SchedulePaymentScreen;
